import { Injectable, inject } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  of,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { MarkedItem, ResponseStateHandler, TaskContextService } from '@comparit/shared';
import { isEqual } from '@comparit/core';
import { Fahrzeugdetails, Kraftstoffart } from '../interfaces';
import { FormsService } from './forms.service';
import { ResultsService } from './results.service';
import { KfzBerechnungvorgaben, KfzErgebnis, KfzFahrzeugService } from '../interfaces/generated';

export type CarDataState = ResponseStateHandler<Fahrzeugdetails>;

/**
 * The payload minus the fields the backend stores but does not price. `calcStartTrigger$`
 * compares on this, so answering a stored-only question cannot restart the whole SignalR run
 * and wipe the customer's comparison list.
 */
export const pricingIdentity = (value: KfzBerechnungvorgaben): KfzBerechnungvorgaben => {
  const { rabattUebertragungGewuenscht, ...sfKlassen } = value.sfKlassen ?? {};

  return { ...value, sfKlassen };
};

@Injectable({ providedIn: 'root' })
export class CalculatorStateService {
  private readonly formsService = inject(FormsService);
  private readonly fahrzeugService = inject(KfzFahrzeugService);
  private readonly resultsService = inject(ResultsService);
  private readonly taskContext = inject(TaskContextService);

  private readonly _inCalculatorPage$ = new BehaviorSubject<boolean>(false);
  readonly inCalculatorPage$ = this._inCalculatorPage$.asObservable();

  private readonly _selectedVehicle$ = new ReplaySubject<CarDataState>(1);
  readonly selectedVehicle$ = this._selectedVehicle$.asObservable();

  private initialized = false;

  init() {
    if (this.initialized) return;
    this.initialized = true;

    this.fetchCarData$.subscribe(response => {
      this._selectedVehicle$.next(response);
      if (response.data) {
        this.formsService.fahrzeugFormCreator.stepsCtrls.step3.patchValue({
          elektrofahrzeug:
            !!response.data.kraftstoffartEnum &&
            [Kraftstoffart.Elektro, Kraftstoffart.Hybrid].includes(response.data.kraftstoffartEnum),
          kraftstoffart: response.data.kraftstoffartEnum,
          motorleistungKw: Math.floor(response.data.motorleistung),
        });
      }
    });

    this.calcStartTrigger$.subscribe();
  }

  enterCalculatorPage(): void {
    this._inCalculatorPage$.next(true);
  }

  leaveCalculatorPage(): void {
    this._inCalculatorPage$.next(false);
  }

  selectVehicle(vehicle: Fahrzeugdetails): void {
    this._selectedVehicle$.next({
      state: 'success',
      error: null,
      data: vehicle,
    });
    this.applyVehicleToForm(vehicle, { emitEvent: false });
  }

  resetSelectedVehicle() {
    this.clearSelectedVehicle();
    this.formsService.resetSelectedVehicle();
  }

  /** No vehicle shown any more, without touching the HSN/TSN fields. */
  clearSelectedVehicle(): void {
    this._selectedVehicle$.next({
      data: null,
      state: 'none',
      error: null,
    });
  }

  private applyVehicleToForm(vehicle: Fahrzeugdetails, opts?: { emitEvent: boolean }): void {
    this.formsService.fahrzeugFormCreator.stepsCtrls.step3.patchValue(
      {
        hsn: vehicle.hsn,
        tsn: vehicle.tsn,
        elektrofahrzeug:
          !!vehicle.kraftstoffartEnum &&
          [Kraftstoffart.Elektro, Kraftstoffart.Hybrid].includes(vehicle.kraftstoffartEnum),
        kraftstoffart: vehicle.kraftstoffartEnum,
        motorleistungKw: Math.floor(vehicle.motorleistung),
      },
      opts
    );
  }

  private fetchCarData$ = this.formsService.fahrzeugFormCreator.step3Form.hsnTsnSearchValue$.pipe(
    tap(() => {
      this._selectedVehicle$.next({
        data: null,
        state: 'loading',
        error: null,
      });
    }),
    switchMap(([hsn, tsn]) =>
      this.fahrzeugService
        .getKfzFahrzeugByHsnTsn({
          kfzFahrzeugByHsnTsnRequest: { hsn: hsn ?? '', tsn: tsn ?? '' },
        })
        .pipe(
          map(
            response =>
              ({
                success: true,
                data: response.fahrzeuge?.[0] ?? null,
                error: null,
                state: 'success',
              }) as CarDataState
          ),
          catchError(error =>
            of({
              data: null,
              error:
                error?.error?.message ??
                'Das Fahrzeug wurde in unserer Fahrzeugliste nicht gefunden. Du kannst trotzdem weiterrechnen — die Versicherer ermitteln das Fahrzeug anhand deiner HSN/TSN-Eingabe selbst.',
              state: 'error',
            } as CarDataState)
          )
        )
    )
  );

  private calcStartTrigger$: Observable<unknown> = this.formsService.journeyReset$.pipe(
    startWith(undefined),
    switchMap(() =>
      combineLatest([
        this.formsService.berechnungFormularValue$,
        this._inCalculatorPage$.pipe(distinctUntilChanged()),
      ]).pipe(
        filter(([, inCalculatorPage]) => inCalculatorPage === true),
        debounceTime(400),
        filter(() => !this.formsService.isCalculationFormInvalid()),
        distinctUntilChanged(([prevValue], [currValue]) =>
          isEqual(pricingIdentity(prevValue), pricingIdentity(currValue))
        ),
        switchMap(([berechnungFormular]) => {
          const oldCompare = [...this.formsService.calculatorForm.form.fgCtrls.compare.value];
          const oldBausteine = [...this.formsService.calculatorForm.form.fgCtrls.bausteine.value];

          this.formsService.calculatorForm.form.fgCtrls.bausteine.setValue([]);
          this.formsService.calculatorForm.form.fgCtrls.compare.setValue([]);

          this.resultsService.start({
            berechnungvorgaben: berechnungFormular,
            taskId: this.taskContext.taskId() ?? undefined,
          });

          return this.resultsService.ergebnisReceived.asObservable().pipe(
            tap(receivedErgebnise => {
              this.reconcileMarkedItems(oldCompare, receivedErgebnise, item =>
                this.formsService.calculatorForm.toggleCompare(item)
              );
              this.reconcileMarkedItems(oldBausteine, receivedErgebnise, item =>
                this.formsService.calculatorForm.toggleBausteine(item)
              );
            })
          );
        })
      )
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private reconcileMarkedItems(
    oldItems: MarkedItem[],
    ergebnise: KfzErgebnis[],
    toggle: (item: MarkedItem) => void
  ): void {
    for (const item of oldItems) {
      const found = ergebnise.find(r => r.kombinationId === item.kombinationId && r.successful);
      if (found) {
        toggle({ ...item, berechnungId: found.berechnungId });
      }
    }
  }
}
